import { useState } from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import type { WasteStatsBucket } from "@/hooks/use-waste-stats";
import { useTranslation } from "@/i18n/use-translation";
import { formatCurrency, formatWeight } from "@/lib/format";
import { foodLabel } from "@/lib/food-labels";

import { ChartCard } from "./chart-card";
import { SegmentedToggle } from "./segmented-toggle";

type Metric = "weight" | "cost";

const METRIC_COLOR = "#3b82f6";

// The stats RPC keys events with no top-level category FK — the unidentified L1
// line included — as this bucket (COALESCE(fc.name, 'Uncategorised')).
const UNCATEGORISED_KEY = "Uncategorised";

/** Chart rows, largest first. The `Uncategorised` bucket is dropped: unidentified
 * waste stays in the KPI/trend totals but is never listed as its own line
 * (POS2-924). Accepted trade-off — the bars then sum to less than the KPI total. */
export function categoryChartData(
  buckets: WasteStatsBucket[],
  metric: Metric,
): { key: string; value: number }[] {
  return buckets
    .filter((b) => b.key !== UNCATEGORISED_KEY)
    .map((b) => ({ key: b.key, value: metric === "weight" ? b.total_weight : b.total_cost }))
    .filter((d) => d.value > 0)
    .sort((a, b) => b.value - a.value);
}

export function CategoryBar({
  buckets,
  currency,
}: {
  buckets: WasteStatsBucket[];
  currency: string;
}) {
  const { locale, t } = useTranslation();
  const [metric, setMetric] = useState<Metric>("weight");

  // The RPC buckets by `food_categories.name`, which is also the axis label and the tick's
  // tooltip — so the reader's wording is applied here rather than inside the pure selector,
  // which stays locale-free (POS2-1134).
  const config: ChartConfig = {
    weight: { label: t("waste.metricWeight"), color: METRIC_COLOR },
    cost: { label: t("waste.metricCost"), color: METRIC_COLOR },
  };
  const data = categoryChartData(buckets, metric).map((d) => ({
    ...d,
    key: foodLabel(t, d.key),
  }));

  const format = (v: number) =>
    metric === "weight" ? formatWeight(locale, v) : formatCurrency(locale, v, currency);

  return (
    <ChartCard
      title={t("waste.categoryBarTitle")}
      description={t("waste.categoryBarDescription")}
      isEmpty={data.length === 0}
      action={
        <SegmentedToggle
          ariaLabel={t("waste.categoryMetricToggle")}
          value={metric}
          onChange={setMetric}
          options={[
            // "kg" is a unit symbol, written the same in every language this app ships.
            { value: "weight", label: "kg" },
            { value: "cost", label: t("waste.metricCost") },
          ]}
        />
      }
    >
      <ChartContainer config={config} className="h-[300px] w-full">
        <BarChart data={data} layout="vertical" margin={{ left: 12, right: 16 }}>
          <CartesianGrid horizontal={false} />
          <YAxis
            type="category"
            dataKey="key"
            width={116}
            tickLine={false}
            axisLine={false}
            tickMargin={6}
            interval={0}
            tick={<CategoryTick />}
          />
          <XAxis type="number" hide />
          <ChartTooltip
            content={
              <ChartTooltipContent hideLabel formatter={(value) => `${format(Number(value))}`} />
            }
          />
          <Bar dataKey="value" fill={METRIC_COLOR} radius={4} />
        </BarChart>
      </ChartContainer>
    </ChartCard>
  );
}

export function truncateCategoryLabel(label: string, max = 16): string {
  return label.length > max ? `${label.slice(0, max - 1)}…` : label;
}

// Single-line Y-axis label. A plain <text> never wraps (unlike Recharts' default
// tick), so long category names no longer stack onto multiple lines and overlap.
// Truncated to fit the axis width; the full name shows in an SVG <title> on hover.
function CategoryTick({
  x,
  y,
  payload,
}: {
  x?: number;
  y?: number;
  payload?: { value?: string | number };
}) {
  const full = String(payload?.value ?? "");
  return (
    <text x={x} y={y} dy={4} textAnchor="end" className="fill-gray-500 text-[11px]">
      {truncateCategoryLabel(full)}
      <title>{full}</title>
    </text>
  );
}
